import { AUTH_ROUTES, CONSENT_REQUEST_PARAM, formatAddress, legalPath, ownerRestPath, REST_ROUTES, restAuthorizationDecisionSchema, restAuthorizationSchema, restBrowseSchema, restConnectClaimedSchema, restConnectDecidedSchema, restConnectStartedSchema, restConnectViewSchema, restErrorSchema, restFeaturedSchema, restFileSchema, restFindSchema, restGrantsSchema, restIndexNowSchema, restLegalDocumentSchema, restMeSchema, restMountSchema, restMyIndexSchema, restMyRepositoriesSchema, restNewRepoTokenSchema, restOwnerSchema, restPath, restRepoTokensSchema, restSearchSchema, restShowcaseSchema, restSkillSchema, } from "@skillcdn/core";
export const DEFAULT_TIMEOUT_MS = 30_000;
/** A mount overview lists at most a couple of hundred skills and documents; a page of a file is up to 100,000 characters. */
export const DEFAULT_MAX_BODY_BYTES = 4 * 1024 * 1024;
/** The API said no, or could not be reached, or answered in a shape this client does not read. */
export class ApiError extends Error {
    /** HTTP status, or 0 when there was no response at all. */
    status;
    /** The stable code from the error body, `network` or `invalid_response`. */
    code;
    /** With `skill.ambiguous`: the directories to choose from. */
    directories;
    /** With `file.too_large`, `file.not_text` and `file.not_served`: where the file is at its host. */
    sourceUrl;
    /** With a `503` of the git host: when to try again. */
    retryAfterSeconds;
    constructor(status, code, message, extra = {}) {
        super(message);
        this.name = "ApiError";
        this.status = status;
        this.code = code;
        this.directories = extra.directories ?? [];
        this.sourceUrl = extra.sourceUrl;
        this.retryAfterSeconds = extra.retryAfterSeconds;
    }
}
/** Which of several query parameters are given, in the order given, as a query string. */
function queryOf(params) {
    const query = new URLSearchParams();
    for (const [name, value] of Object.entries(params)) {
        if (value !== undefined) {
            query.set(name, String(value));
        }
    }
    const text = query.toString();
    return text.length === 0 ? "" : `?${text}`;
}
export function createClient(options) {
    const send = options.fetch ?? ((input, init) => fetch(input, init));
    const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
    const maxBodyBytes = options.maxBodyBytes ?? DEFAULT_MAX_BODY_BYTES;
    const base = options.baseUrl.replace(/\/+$/, "");
    const headersOf = (body) => ({
        accept: "application/json",
        ...(body ? { "content-type": "application/json" } : {}),
        ...(options.token === undefined ? {} : { authorization: `Bearer ${options.token}` }),
        ...(options.userAgent === undefined ? {} : { "user-agent": options.userAgent }),
    });
    /** One request, and its body as the API's JSON; what went wrong is an ApiError. */
    const exchange = async (method, path, body, signal) => {
        const timeout = AbortSignal.timeout(timeoutMs);
        let response;
        try {
            response = await send(`${base}${path}`, {
                method,
                headers: headersOf(body !== undefined),
                ...(body === undefined ? {} : { body: JSON.stringify(body) }),
                // A token goes to the origin it was made for and nowhere it is sent on to.
                redirect: "error",
                signal: signal === undefined ? timeout : AbortSignal.any([signal, timeout]),
            });
        }
        catch (error) {
            if (signal?.aborted === true) {
                throw error;
            }
            throw new ApiError(0, "network", timeout.aborted ? "The server did not answer in time." : "The server could not be reached.");
        }
        const declared = Number(response.headers.get("content-length") ?? "0");
        if (declared > maxBodyBytes) {
            await response.body?.cancel();
            throw new ApiError(response.status, "invalid_response", "The server answered more than is read.");
        }
        let text;
        try {
            text = await response.text();
        }
        catch (error) {
            if (signal?.aborted === true) {
                throw error;
            }
            throw new ApiError(0, "network", "The server stopped answering.");
        }
        if (text.length > maxBodyBytes) {
            throw new ApiError(response.status, "invalid_response", "The server answered more than is read.");
        }
        let json;
        try {
            json = text.length === 0 ? undefined : JSON.parse(text);
        }
        catch {
            json = undefined;
        }
        if (!response.ok) {
            const parsed = restErrorSchema.safeParse(json);
            const retryAfter = Number(response.headers.get("retry-after") ?? "");
            throw parsed.success
                ? new ApiError(response.status, parsed.data.error.code, parsed.data.error.message, {
                    directories: parsed.data.error.directories,
                    sourceUrl: parsed.data.error.sourceUrl,
                    retryAfterSeconds: Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : undefined,
                })
                : new ApiError(response.status, "invalid_response", "The server answered unexpectedly.");
        }
        return { response, json };
    };
    const read = async (path, schema, signal) => {
        const { response, json } = await exchange("GET", path, undefined, signal);
        const parsed = schema.safeParse(json);
        if (!parsed.success) {
            throw new ApiError(response.status, "invalid_response", "The server answered unexpectedly.");
        }
        return parsed.data;
    };
    const write = async (method, path, body, schema) => {
        const { response, json } = await exchange(method, path, body, undefined);
        if (schema === undefined) {
            return undefined;
        }
        const parsed = schema.safeParse(json);
        if (!parsed.success) {
            throw new ApiError(response.status, "invalid_response", "The server answered unexpectedly.");
        }
        return parsed.data;
    };
    return {
        mount: (address, signal) => read(restPath("mounts", address), restMountSchema, signal),
        browse: (address, query, signal) => read(`${restPath("browse", address)}${queryOf({ path: query.path, cursor: query.cursor, limit: query.limit })}`, restBrowseSchema, signal),
        find: (address, query, signal) => read(`${restPath("find", address)}${queryOf({ query: query.query, path: query.path, cursor: query.cursor, limit: query.limit })}`, restFindSchema, signal),
        skill: (address, query, signal) => read(`${restPath("skills", address)}${queryOf({ path: query.path, cursor: query.cursor })}`, restSkillSchema, signal),
        file: (address, query, signal) => read(`${restPath("files", address)}${queryOf({ path: query.path, offset: query.offset, limit: query.limit })}`, restFileSchema, signal),
        featured: (signal) => read(REST_ROUTES.featured, restFeaturedSchema, signal),
        search: (query, limit, signal) => read(`${REST_ROUTES.search}${queryOf({ query, limit })}`, restSearchSchema, signal),
        showcase: (signal) => read(REST_ROUTES.showcase, restShowcaseSchema, signal),
        legal: (kind, signal) => read(legalPath(kind), restLegalDocumentSchema, signal),
        owner: (owner, page, signal) => read(`${ownerRestPath(owner)}${queryOf({ page: page === 1 ? undefined : page })}`, restOwnerSchema, signal),
        me: (signal) => read(REST_ROUTES.me, restMeSchema, signal),
        myIndex: (address, signal) => read(`${REST_ROUTES.me}/index${formatAddress(address)}`, restMyIndexSchema, signal),
        indexNow: async (address) => {
            const asked = await write("POST", `${REST_ROUTES.me}/index${formatAddress(address)}`, undefined, restIndexNowSchema);
            if (asked === undefined) {
                throw new ApiError(0, "invalid_response", "The server answered unexpectedly.");
            }
            return asked;
        },
        myRepositories: (signal) => read(`${REST_ROUTES.me}/repositories`, restMyRepositoriesSchema, signal),
        myGrants: (signal) => read(`${REST_ROUTES.me}/grants`, restGrantsSchema, signal),
        removeGrant: async (id) => {
            await write("DELETE", `${REST_ROUTES.me}/grants/${encodeURIComponent(id)}`, undefined, undefined);
        },
        myTokens: (signal) => read(`${REST_ROUTES.me}/tokens`, restRepoTokensSchema, signal),
        makeToken: async (request) => {
            const made = await write("POST", `${REST_ROUTES.me}/tokens`, request, restNewRepoTokenSchema);
            if (made === undefined) {
                throw new ApiError(0, "invalid_response", "The server answered unexpectedly.");
            }
            return made;
        },
        removeToken: async (id) => {
            await write("DELETE", `${REST_ROUTES.me}/tokens/${encodeURIComponent(id)}`, undefined, undefined);
        },
        signOut: async () => {
            await write("POST", AUTH_ROUTES.logout, undefined, undefined);
        },
        authorization: (request, signal) => read(`${REST_ROUTES.authorization}${queryOf({ [CONSENT_REQUEST_PARAM]: request })}`, restAuthorizationSchema, signal),
        decide: async (request, approve) => {
            const answer = await write("POST", REST_ROUTES.decision, { request, approve }, restAuthorizationDecisionSchema);
            if (answer === undefined) {
                throw new ApiError(200, "invalid_response", "The server answered unexpectedly.");
            }
            return answer.redirect;
        },
        connect: async (request, signal) => {
            const { response, json } = await exchange("POST", REST_ROUTES.connect, request, signal);
            const parsed = restConnectStartedSchema.safeParse(json);
            if (!parsed.success) {
                throw new ApiError(response.status, "invalid_response", "The server answered unexpectedly.");
            }
            return parsed.data;
        },
        claimConnection: async (secret, signal) => {
            const { response, json } = await exchange("POST", `${REST_ROUTES.connect}/claim`, { secret }, signal);
            const parsed = restConnectClaimedSchema.safeParse(json);
            if (!parsed.success) {
                throw new ApiError(response.status, "invalid_response", "The server answered unexpectedly.");
            }
            return parsed.data;
        },
        connection: (code, signal) => read(`${REST_ROUTES.connect}/${encodeURIComponent(code)}`, restConnectViewSchema, signal),
        decideConnection: async (code, decision) => {
            const answer = await write("POST", `${REST_ROUTES.connect}/${encodeURIComponent(code)}/decision`, decision, restConnectDecidedSchema);
            if (answer === undefined) {
                throw new ApiError(200, "invalid_response", "The server answered unexpectedly.");
            }
            return answer;
        },
    };
}
//# sourceMappingURL=client.js.map